/**
 * Subscription Service
 * 
 * Handles all subscription-related operations
 * Using REAL API for create operation, MOCK API for others
 */

import { mockSubscriptionsAPI, subscriptionsAPI } from "../api/subscriptions";
import { SubscriptionContract } from "../contracts/subscription";
import { ProductContract } from "../contracts/product";

/**
 * Subscription Service Class
 * Uses MOCK API for all operations
 */
export class SubscriptionService {
    constructor() { }

    // ========================================================================
    // SUBSCRIPTION OPERATIONS (Using Real API)
    // ========================================================================

    /**
     * Get all subscriptions for a user (REAL API)
     * GET /users/self/subscriptions
     */
    public async getUserSubscriptions(): Promise<SubscriptionContract[]> {
        return subscriptionsAPI.getUserSubscriptions();
    }

    /**
     * Get a single subscription by ID (REAL API)
     * GET /users/self/subscriptions/{subscriptionId}
     */
    public async getSubscription(subscriptionId: string): Promise<SubscriptionContract> {
        return subscriptionsAPI.getSubscription(subscriptionId);
    }

    // ========================================================================
    // SUBSCRIPTION MUTATIONS (Using Real API)
    // ========================================================================

    /**
     * Create a new subscription (REAL API)
     * POST /users/self/subscriptions
     * Payload: { "displayName": "subscriptionName" }
     */
    public async createSubscription(
        subscriptionId: string,
        productId: string,
        subscriptionName: string
    ): Promise<SubscriptionContract> {
        // Use the real API - it only needs displayName
        return subscriptionsAPI.createSubscription(subscriptionName);
    }

    /**
     * Cancel a subscription (REAL API)
     * DELETE /users/self/subscriptions/{subscriptionId}
     */
    public async cancelSubscription(subscriptionId: string): Promise<void> {
        return subscriptionsAPI.cancelSubscription(subscriptionId);
    }

    /**
     * Rename a subscription (REAL API)
     * PUT /users/self/subscriptions/{subscriptionId}
     * Payload: { "displayName": "newName" }
     */
    public async renameSubscription(subscriptionId: string, newName: string): Promise<void> {
        await subscriptionsAPI.renameSubscription(subscriptionId, newName);
    }

    /**
     * Regenerate subscription key (MOCK)
     * TODO: Replace with real API when endpoint is available
     */
    public async regenerateKey(
        subscriptionId: string,
        keyType: 'primary' | 'secondary'
    ): Promise<void> {
        await subscriptionsAPI.regenerateKey(subscriptionId, keyType);
    }

    // ========================================================================
    // PRODUCT OPERATIONS (Using Mock API - TODO: migrate to real API)
    // ========================================================================

    /**
     * Get all products (MOCK)
     * TODO: Replace with real API when endpoint is available
     */
    public async getProducts(): Promise<ProductContract[]> {
        return subscriptionsAPI.getProducts();
    }

    // ========================================================================
    // UTILITY METHODS
    // ========================================================================

    /**
     * Generate a unique subscription ID
     */
    public generateSubscriptionId(): string {
        const timestamp = Date.now();
        const randomStr = Math.random().toString(36).substring(2, 9);
        return `sub-${timestamp}-${randomStr}`;
    }

    // ========================================================================
    // STATIC METHODS (for backward compatibility)
    // ========================================================================

    static async getUserSubscriptions(): Promise<SubscriptionContract[]> {
        const service = new SubscriptionService();
        return service.getUserSubscriptions();
    }

    static async getSubscription(subscriptionId: string): Promise<SubscriptionContract> {
        const service = new SubscriptionService();
        return service.getSubscription(subscriptionId);
    }

    static async createSubscription(
        userId: string,
        subscriptionId: string,
        productId: string,
        subscriptionName: string
    ): Promise<SubscriptionContract> {
        const service = new SubscriptionService();
        return service.createSubscription(subscriptionId, productId, subscriptionName);
    }

    static async cancelSubscription(subscriptionId: string): Promise<void> {
        const service = new SubscriptionService();
        return service.cancelSubscription(subscriptionId);
    }

    static async renameSubscription(subscriptionId: string, newName: string): Promise<void> {
        const service = new SubscriptionService();
        return service.renameSubscription(subscriptionId, newName);
    }

    static async regenerateKey(subscriptionId: string, keyType: 'primary' | 'secondary'): Promise<void> {
        const service = new SubscriptionService();
        return service.regenerateKey(subscriptionId, keyType);
    }

    static async getProducts(): Promise<ProductContract[]> {
        const service = new SubscriptionService();
        return service.getProducts();
    }

    static generateSubscriptionId(): string {
        const service = new SubscriptionService();
        return service.generateSubscriptionId();
    }
}

export default SubscriptionService;
